import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import HeroBanner from "../components/heroBanner";
import ProductCard from "../components/productCard";
import type { Product, ProductVariant } from "../types";
import { groupVariants } from "../utils/groupVariants";

const ShopPage = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  const q = searchParams.get("q")?.toLowerCase().trim() ?? "";
  const visible = q
    ? products.filter((p) => p.name.toLowerCase().includes(q))
    : products;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const res = await fetch(`${apiUrl}/api/v1/products`);
        if (res.ok) {
          const variants: ProductVariant[] = await res.json();
          setProducts(groupVariants(variants));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <Box component="main">
      <HeroBanner />

      <Box
        id="shop"
        sx={{
          maxWidth: "80rem",
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: { xs: 6, md: 10 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            mb: 6,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {q ? `Results for "${q}"` : "All Products"}
          </Typography>
          {!loading && (
            <Typography variant="caption" sx={{ color: "#737373" }}>
              {visible.length} {visible.length === 1 ? "item" : "items"}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: "black" }} />
          </Box>
        ) : visible.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 16 }}>
            <Typography variant="body2" sx={{ color: "#737373" }}>
              {q
                ? `No products match "${q}".`
                : "No products are currently available."}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: { xs: 3, md: 4 },
            }}
          >
            {visible.map((product) => (
              <ProductCard key={product.name} product={product} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ShopPage;
